import SlideInPane from "patient-ping-remedy/packages/slide-in-pane";
import React, {useContext, useEffect} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import Button from "patient-ping-remedy/packages/button";
import {EventConsumer} from "@/api/dto/dto";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import Helpers from "@/helpers/helpers";
import {AxiosError} from "axios";
import EventConsumerForm from "@/components/pages/settings/event_consumers/EventConsumerForm";
import {createFormFieldConfig, useForm} from "@/hooks/useForm";
import Validate from "@/helpers/validate";
import Loading from "@common/Loading";

type Props = {
    isOpen: boolean,
    onClose: () => void,
    eventConsumerId: Nullable<number>
}

const EventConsumerDrawer = (props: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const {form, setInitialForm, handleChange, handleSubmit, isValid, isFormChanged, submitted, resetForm} = useForm(submit);
  const [inFlight, setInFlight] = React.useState(false);
  const queryClient = useQueryClient();
  const {addAlert} = useAlertStore();

  const {data : eventConsumer, isLoading} = useQuery({
    queryKey: ['eventConsumers', props.eventConsumerId],
    queryFn: () => carecoApi?.getEventConsumer(props.eventConsumerId!),
    enabled: !!carecoApi && !!props.eventConsumerId
  });

  const {mutate} = useMutation({
    mutationFn: async (payload: EventConsumer) => carecoApi?.updateEventConsumer(payload),
    onSuccess: () => {
      addAlert({content: "Event consumer updated successfully.", type: ToastType.SUCCESS});
      queryClient.invalidateQueries({queryKey: ['eventConsumers']}).then();
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to update event consumer. ${Helpers.traceId(error)}`, error);
      setInFlight(false);
    }
  });

  useEffect(() => {
    if(eventConsumer) {
      setInitialForm({
        ...createFormFieldConfig(
          "name",
          eventConsumer?.name,
          {valid: true, message: ''},
          [Validate.required],
          true
        ),
        ...createFormFieldConfig(
          "filters",
          eventConsumer?.filters,
          {valid: true, message: ''},
          [Validate.required],
          true
        )
      });
    }
    setInFlight(false);
  }, [eventConsumer, setInitialForm]);

  function submit() {
    const payload: EventConsumer = {
      id: props.eventConsumerId,
      name: form.name.value,
      filters: form.filters.value,
    };
    mutate(payload);
    setInFlight(true);
  }

  const onClose = () => {
    resetForm();
    props.onClose();
  };

  return (
    <SlideInPane
      isOpen={props.isOpen}
      onClose={onClose}
      showNav={false}
      headerText={"Event consumer details"}
      buttons={[
        <Button
          key="saveButton"
          onClick={handleSubmit}
          disabled={
            !isValid() || !isFormChanged() || submitted || inFlight
            || form?.filters?.value?.eventStates?.values.length === 0
            || form?.filters?.value?.eventTypes?.values.length === 0
          }
        >
        Save
        </Button>
      ]}
    >

      <br/><br/>
      {isLoading || !form ? <Loading/> :
        <EventConsumerForm form={form} handleChange={handleChange} eventConsumer={eventConsumer}/>
      }
    </SlideInPane>);
};

export default EventConsumerDrawer;
